'use client';

import type { Options } from '@contentful/rich-text-react-renderer';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import clsx from 'clsx';
import type { Block, Document, Inline, Text as ContentfulText } from '@contentful/rich-text-types';
import { BLOCKS } from '@contentful/rich-text-types';
import type { ReactNode } from 'react';
import { COOKIE_POLICY_PLACEHOLDER } from './richText';

export const CONTACT_PHONE_PLACEHOLDER = '%{{contact.phone}}%';
export const CONTACT_EMAIL_PLACEHOLDER = '%{{contact.email}}%';
export const COOKIE_SETTINGS_PLACEHOLDER = '{{InsertCookieSettings}}';

export const CONTACT_PLACEHOLDER_REGEX = /(%\{\{contact\.phone\}\}%|%\{\{contact\.email\}\}%)/g;

export const DYNAMIC_PLACEHOLDER_REGEX =
  /(\{\{InsertCookieSettings\}\}|%\{\{contact\.phone\}\}%|%\{\{contact\.email\}\}%|https?:\/\/[^\s]+)/g;

export const CONTACT_AND_URL_PLACEHOLDER_REGEX =
  /(%\{\{contact\.phone\}\}%|%\{\{contact\.email\}\}%|https?:\/\/[^\s]+)/g;

export interface DynamicContactInfo {
  email?: string;
  phoneNumber?: string;
}

export interface ProcessTextWithPlaceholdersOptions {
  contactInfo?: DynamicContactInfo;
  onPhoneClick?: () => void;
  onEmailClick?: () => void;
  linkClassName?: string;
  urlLinkClassName?: string;
  cookieButtonClassName?: string;
  includeCookieSettings?: boolean;
  autoLinkUrls?: boolean;
}

export function getParagraphTextValue(node: Block | Inline): string {
  return (
    node.content
      ?.map((child) => ('value' in child ? (child as ContentfulText).value : ''))
      .join('') ?? ''
  );
}

export function hasContactPlaceholders(node: Block | Inline): boolean {
  const textValue = getParagraphTextValue(node);

  return (
    textValue.includes(CONTACT_PHONE_PLACEHOLDER) || textValue.includes(CONTACT_EMAIL_PLACEHOLDER)
  );
}

export function hasCookiePolicyPlaceholder(node: Block | Inline): boolean {
  return getParagraphTextValue(node).includes(COOKIE_POLICY_PLACEHOLDER);
}

/**
 * Returns rich-text nodes that appear after {{InsertCookiePolicy}} in a block.
 */
export function splitContentAfterCookiePolicy(
  content: Block['content'] | undefined
): Block['content'] {
  if (!content) return [];

  let placeholderConsumed = false;
  const trailing: Block['content'] = [];

  for (const child of content) {
    if (placeholderConsumed) {
      trailing.push(child);
      continue;
    }

    if (child.nodeType === 'text' && 'value' in child) {
      const textNode = child as ContentfulText;
      const placeholderIndex = textNode.value.indexOf(COOKIE_POLICY_PLACEHOLDER);
      if (placeholderIndex === -1) continue;

      placeholderConsumed = true;
      const textAfterPlaceholder = textNode.value
        .slice(placeholderIndex + COOKIE_POLICY_PLACEHOLDER.length)
        .replace(/^\s+/, '');

      if (textAfterPlaceholder) {
        trailing.push({ ...textNode, value: textAfterPlaceholder });
      }
    }
  }

  return trailing;
}

interface CookiePolicyBlockProps {
  node: Block | Inline;
  options: Options;
}

const COOKIE_POLICY_HTML = { __html: '' };
const COOKIE_SETTINGS_HTML = { __html: 'Cookie Settings' };

/**
 * Mount point OneTrust fills with the cookie policy table, followed by any text
 * authored after the placeholder in the same paragraph.
 */
export function CookiePolicyBlock({ node, options }: Readonly<CookiePolicyBlockProps>) {
  const trailingContent = splitContentAfterCookiePolicy(node.content as Block['content']);
  const trailingDocument: Document = {
    nodeType: BLOCKS.DOCUMENT,
    data: {},
    content: [{ nodeType: BLOCKS.PARAGRAPH, data: {}, content: trailingContent }],
  };

  return (
    <>
      <div
        id="ot-sdk-cookie-policy"
        dangerouslySetInnerHTML={COOKIE_POLICY_HTML}
        suppressHydrationWarning
      />
      {trailingContent.length > 0 && documentToReactComponents(trailingDocument, options)}
    </>
  );
}

export function hasDynamicPlaceholders(node: Block | Inline): boolean {
  const textValue = getParagraphTextValue(node);

  return (
    textValue.includes(COOKIE_SETTINGS_PLACEHOLDER) ||
    textValue.includes(CONTACT_PHONE_PLACEHOLDER) ||
    textValue.includes(CONTACT_EMAIL_PLACEHOLDER)
  );
}

export function CookieSettingsButton({ className }: Readonly<{ className?: string }>) {
  // Bound by OneTrust SDK + bindCookieSettingsButtons() after soft nav — no React onClick.
  return (
    <button
      type="button"
      className={clsx(className, 'ot-sdk-show-settings')}
      dangerouslySetInnerHTML={COOKIE_SETTINGS_HTML}
      suppressHydrationWarning
    />
  );
}

function getPlaceholderRegex(options: ProcessTextWithPlaceholdersOptions): RegExp {
  const includeCookieSettings = options.includeCookieSettings ?? true;
  const autoLinkUrls = options.autoLinkUrls ?? true;

  if (includeCookieSettings && autoLinkUrls) {
    return DYNAMIC_PLACEHOLDER_REGEX;
  }

  if (!includeCookieSettings && autoLinkUrls) {
    return CONTACT_AND_URL_PLACEHOLDER_REGEX;
  }

  return CONTACT_PLACEHOLDER_REGEX;
}

export function processTextWithPlaceholders(
  text: string,
  options: ProcessTextWithPlaceholdersOptions = {}
): ReactNode[] {
  const {
    contactInfo,
    onPhoneClick,
    onEmailClick,
    linkClassName,
    urlLinkClassName,
    cookieButtonClassName,
    includeCookieSettings = true,
    autoLinkUrls = true,
  } = options;

  const regex = getPlaceholderRegex({ includeCookieSettings, autoLinkUrls });
  const parts = text.split(regex);
  const externalLinkClass = urlLinkClassName ?? linkClassName;

  return parts.map((part, index) => {
    if (part === CONTACT_PHONE_PLACEHOLDER && contactInfo?.phoneNumber) {
      return (
        <a
          key={`contact-phone-${index}`}
          href={`tel:${contactInfo.phoneNumber.replace(/\s/g, '')}`}
          className={linkClassName}
          onClick={onPhoneClick}
        >
          <strong>{contactInfo.phoneNumber}</strong>
        </a>
      );
    }

    if (part === CONTACT_EMAIL_PLACEHOLDER && contactInfo?.email) {
      return (
        <a
          key={`contact-email-${index}`}
          href={`mailto:${contactInfo.email}`}
          className={linkClassName}
          onClick={onEmailClick}
        >
          <strong>{contactInfo.email}</strong>
        </a>
      );
    }

    if (part === COOKIE_SETTINGS_PLACEHOLDER && includeCookieSettings) {
      return (
        <CookieSettingsButton key={`cookie-settings-${index}`} className={cookieButtonClassName} />
      );
    }

    if (autoLinkUrls && /^https?:\/\/[^\s]+$/.test(part)) {
      return (
        <a
          key={`url-link-${index}`}
          href={part}
          rel="noopener noreferrer"
          target="_blank"
          className={externalLinkClass}
        >
          {part}
        </a>
      );
    }

    if (!part) {
      return null;
    }

    return part;
  });
}

export function processNodeTextWithPlaceholders(
  node: Block | Inline,
  options: ProcessTextWithPlaceholdersOptions = {}
): ReactNode[] {
  const elements: ReactNode[] = [];

  node.content?.forEach((child, index) => {
    if ('value' in child && (child as ContentfulText).value) {
      const parts = processTextWithPlaceholders((child as ContentfulText).value, options);

      parts.forEach((part, partIndex) => {
        if (typeof part === 'string') {
          if (part) {
            elements.push(part);
          }
        } else if (part) {
          elements.push(<span key={`dynamic-${index}-${partIndex}`}>{part}</span>);
        }
      });
    }
  });

  return elements;
}
